import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { API, useApp } from "@/AppContext";
import { Briefcase, MapPin, Clock, Truck, Tag, ArrowLeft, MapTrifold, Handshake, ArrowSquareOut } from "@phosphor-icons/react";
import TranslatedText from "@/components/TranslatedText";
import TagLabel from "@/components/TagLabel";
import FbGroupInvite from "@/components/FbGroupInvite";
import { ShareButtons } from "@/components/ShareButtons";
import { cityFullName, cityFromSlug } from "@/data/cities";
import { SKILL_TRANSLATIONS, translations } from "@/i18n";
import { skillBlurb, skillJobs } from "@/data/skillContent";

function setMetaTag(sel, attr, key, value) {
  let el = document.querySelector(sel);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}
function setCanonical(href) {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

export default function CityJobs() {
  const { citySlug, skillSlug } = useParams();
  const { authHeaders, t: siteT, lang: siteLang, langExplicit, setMarket } = useApp();
  const [city, setCity] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [externalJobs, setExternalJobs] = useState([]);
  const [partnerTotal, setPartnerTotal] = useState(0);
  const [intro, setIntro] = useState("");
  const [nearby, setNearby] = useState([]);
  const [supply, setSupply] = useState(null);
  const [loading, setLoading] = useState(true);
  // Puerto Rico & Mexico are Spanish-first unless the visitor explicitly picked a language.
  const cityState = (city?.state || "").toUpperCase();
  const isSpanishFirst = cityState === "PR" || cityState === "MX";
  const lang = (isSpanishFirst && !langExplicit) ? "es" : siteLang;
  const t = translations[lang] || siteT;
  const sk = SKILL_TRANSLATIONS[lang] || SKILL_TRANSLATIONS.en;
  useEffect(() => {
    if (cityState) setMarket(cityState === "MX" ? "mx" : "us");
  }, [cityState, setMarket]);
  const skillKey = skillSlug || null;
  const skillLabel = skillKey ? (sk[skillKey] || skillKey.replace(/_/g, " ")) : null;

  // Nearby cities for internal-linking + worker discovery
  useEffect(() => {
    let cancelled = false;
    setNearby([]);
    setSupply(null);
    axios.get(`${API}/cities/${citySlug}/nearby?limit=8`)
      .then((r) => { if (!cancelled) setNearby(r.data.cities || []); })
      .catch(() => { if (!cancelled) setNearby([]); });
    axios.get(`${API}/cities/${citySlug}/supply`, { params: skillSlug ? { skill: skillSlug } : undefined })
      .then((r) => { if (!cancelled) setSupply(r.data); })
      .catch(() => { if (!cancelled) setSupply(null); });
    return () => { cancelled = true; };
  }, [citySlug, skillSlug]);

  // Resolve city by slug from API — on any failure, fall back to a slug-derived
  // city so the page always renders (partner jobs still load). We never dead-end.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/cities/${citySlug}`)
      .then((r) => {
        if (cancelled) return;
        setCity(r.data);
      })
      .catch(() => {
        if (cancelled) return;
        setCity(cityFromSlug(citySlug));
      });
    return () => { cancelled = true; };
  }, [citySlug]);

  // Fetch jobs for resolved city
  useEffect(() => {
    if (!city) return;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/jobs`, { params: { city: city.name, country: (city.state || "").toUpperCase() === "MX" ? "MX" : "US" }, headers: authHeaders() })
      .then((r) => !cancelled && setJobs(r.data.jobs || []))
      .finally(() => !cancelled && setLoading(false));
    // ALL retained partner listings for this city, read from our DB — this city page
    // is the coverage hub, so every persisted partner job is linked here (zero orphans).
    setExternalJobs([]);
    setPartnerTotal(0);
    axios
      .get(`${API}/cities/${city.slug}/partner-jobs`, { params: { skip: 0, limit: 100 } })
      .then((r) => {
        if (cancelled) return;
        setExternalJobs(r.data.jobs || []);
        setPartnerTotal(r.data.total || 0);
      })
      .catch(() => !cancelled && setExternalJobs([]));
    return () => { cancelled = true; };
  }, [city]); // eslint-disable-line

  const loadMorePartner = () => {
    axios
      .get(`${API}/cities/${city.slug}/partner-jobs`, { params: { skip: externalJobs.length, limit: 100 } })
      .then((r) => setExternalJobs((prev) => [...prev, ...(r.data.jobs || [])]))
      .catch(() => {});
  };

  // Unique, cached, per-city SEO intro (falls back to the generic blurb if empty).
  useEffect(() => {
    if (!city) return;
    let cancelled = false;
    setIntro("");
    axios
      .get(`${API}/cities/${city.slug}/intro`, { params: { lang } })
      .then((r) => !cancelled && setIntro(r.data.intro || ""))
      .catch(() => !cancelled && setIntro(""));
    return () => { cancelled = true; };
  }, [city, lang]);

  // SEO meta tags
  useEffect(() => {
    if (!city || typeof document === "undefined") return;
    const fullName = cityFullName(city);
    // /jobs/in/... is an English-only route (no /es/ jobs URL), so its META must always be
    // English — except genuinely Spanish-first markets (PR/MX). This prevents an English
    // URL from rendering a Spanish <title>/description just because the visitor toggled ES.
    const metaLang = isSpanishFirst ? lang : "en";
    const skMeta = SKILL_TRANSLATIONS[metaLang] || SKILL_TRANSLATIONS.en;
    const skl = skillSlug ? (skMeta[skillSlug] || skillSlug.replace(/_/g, " ")) : null;
    const title = skl
      ? (metaLang === "es" ? `Trabajos de ${skl} en ${fullName} | ChambaNow` : `${skl} Jobs in ${fullName} | ChambaNow`)
      : (metaLang === "es" ? `Trabajos de jornalero y oficios en ${fullName} | ChambaNow` : `Day Labor & Skilled Trades Jobs in ${fullName} | ChambaNow`);
    const genericDesc = skl
      ? (metaLang === "es" ? `Encuentra trabajos de ${skl} en ${fullName}. Postúlate gratis y trabaja directamente con quien contrata, sin comisiones.` : `Find ${skl} jobs in ${fullName}. Apply free and work directly with hirers — no fees. New local ${skl.toLowerCase()} work posted regularly.`)
      : (metaLang === "es" ? `Encuentra y contrata jornaleros y trabajadores de oficios en ${fullName}. Construcción, jardinería, mudanzas, limpieza y más. Publica trabajos gratis o navega en español.` : `Find and hire day labor and skilled trades workers in ${fullName}. Construction, landscaping, moving, cleaning, and more. Post jobs free or browse in English & Spanish.`);
    // Only reuse the fetched city intro as the description when it matches the meta
    // language (the intro is fetched in the visitor's `lang`).
    const desc = (!skl && intro && metaLang === lang) ? intro.slice(0, 158) : genericDesc;
    document.title = title;
    setMetaTag('meta[name="description"]', "name", "description", desc);
    const canonical = skillSlug ? `https://chambanow.com/jobs/in/${city.slug}/${skillSlug}` : `https://chambanow.com/jobs/in/${city.slug}`;
    setMetaTag('meta[property="og:title"]', "property", "og:title", title);
    setMetaTag('meta[property="og:description"]', "property", "og:description", desc);
    setMetaTag('meta[property="og:url"]', "property", "og:url", canonical);
    setMetaTag('meta[property="og:type"]', "property", "og:type", "website");
    const cardUrl = `https://chambanow.com/api/og/city/${city.slug}.png`;
    setMetaTag('meta[property="og:image"]', "property", "og:image", cardUrl);
    setMetaTag('meta[property="og:image:width"]', "property", "og:image:width", "1200");
    setMetaTag('meta[property="og:image:height"]', "property", "og:image:height", "630");
    setMetaTag('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    setMetaTag('meta[name="twitter:title"]', "name", "twitter:title", title);
    setMetaTag('meta[name="twitter:description"]', "name", "twitter:description", desc);
    setMetaTag('meta[name="twitter:image"]', "name", "twitter:image", cardUrl);
    setCanonical(canonical);
  }, [city, lang, intro, skillSlug]);

  // Supply-gated indexability: thin/empty city (and unsupported skill) pages are
  // noindex so Google stops indexing-then-dropping them; supply-backed pages stay
  // indexable. Fail-open to index while supply is loading / if the call failed.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const indexable = supply == null || supply.seo_indexable !== false;
    setMetaTag('meta[name="robots"]', "name", "robots", indexable ? "index, follow" : "noindex, follow");
  }, [supply]);

  if (!city) {
    return <div className="max-w-md mx-auto px-4 py-20 text-center text-zinc-400">{t.jobs.loading}</div>;
  }

  const fullName = cityFullName(city);
  const blurb = lang === "es"
    ? `Trabajos activos de jornaleros, construcción, jardinería, mudanzas y más en ${fullName}. Negocia tu tarifa directamente con quien contrata.`
    : `Active day-labor, construction, landscaping, moving and more in ${fullName}. Negotiate your rate directly with the hirer.`;
  const _needle = skillLabel ? skillLabel.toLowerCase() : "";
  const _matchJob = (j) => !skillKey || j.industry === skillKey || `${j.title || ""} ${j.description || ""} ${j.industry || ""}`.toLowerCase().includes(_needle);
  const _matchPartner = (e) => !skillKey || `${e.title || ""} ${e.snippet || ""} ${e.type || ""}`.toLowerCase().includes(_needle);
  const shownJobs = skillKey ? jobs.filter(_matchJob) : jobs;
  const shownPartner = skillKey ? externalJobs.filter(_matchPartner) : externalJobs;
  const _inAround = lang === "es" ? "en" : "in and around";
  const skillDesc = skillKey
    ? `${skillBlurb(lang, skillKey)} ${_inAround} ${fullName}.` +
      (supply?.available_count ? (lang === "es" ? ` Hay ${supply.available_count} trabajadores de este oficio cerca${supply?.avg_starting_rate ? `, con tarifas desde alrededor de $${supply.avg_starting_rate}/hora` : ""}.` : ` ${supply.available_count} local workers with this skill are nearby${supply?.avg_starting_rate ? `, with rates starting around $${supply.avg_starting_rate}/hr` : ""}.`) : "") +
      (lang === "es" ? " Postúlate gratis y coordina directamente con quien contrata, sin comisiones." : " Apply free and arrange the work directly with the hirer — no fees.")
    : "";
  const _payStart = supply?.avg_starting_rate || null;
  const skillDesc2 = skillKey
    ? (lang === "es"
        ? `Los trabajos comunes de ${skillLabel.toLowerCase()} incluyen ${skillJobs(lang, skillKey)}. ${_payStart ? `En ${fullName}, este trabajo suele empezar alrededor de $${_payStart}/hora` : `El pago en ${fullName} varía según el alcance y la experiencia`}, y se acuerda directamente con quien contrata.`
        : `Common ${skillLabel.toLowerCase()} jobs include ${skillJobs(lang, skillKey)}. ${_payStart ? `In ${fullName}, this work typically starts around $${_payStart}/hr` : `Pay in ${fullName} varies by scope and experience`}, agreed directly with the hirer.`)
    : "";

  const listItems = [
    ...shownJobs.map((j) => ({ url: `https://chambanow.com/jobs/${j.job_id}`, name: j.title })),
    ...shownPartner.map((e) => ({ url: `https://chambanow.com/partner-jobs/${e.slug}`, name: e.title })),
  ].filter((it) => it.url && it.name).slice(0, 25);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": `Jobs in ${fullName}`,
      "url": `https://chambanow.com/jobs/in/${city.slug}`,
      "numberOfItems": listItems.length,
      "itemListElement": listItems.map((it, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "url": it.url,
        "name": it.name,
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Jobs", "item": "https://chambanow.com/jobs" },
        { "@type": "ListItem", "position": 2, "name": fullName, "item": `https://chambanow.com/jobs/in/${city.slug}` },
      ],
    },
  ];

  const _avg = supply?.avg_starting_rate;
  const _skillLabels = (supply?.top_skills || []).map((x) => sk[x.skill] || x.skill.replace(/_/g, " "));
  const _activeCount = skillKey ? (shownJobs.length + shownPartner.length) : (jobs.length + partnerTotal);
  const faqs = lang === "es" ? [
    { q: `¿Qué trabajos de jornalero y oficios hay en ${fullName}?`,
      a: `ChambaNow tiene ${_activeCount} trabajos activos de jornalero, oficios y servicios en ${fullName} y sus alrededores${_skillLabels.length ? `, con demanda de ${_skillLabels.slice(0, 3).join(", ")}` : ""}. Se publican nuevos trabajos locales con frecuencia.` },
    { q: `¿Cuánto pagan los trabajos de jornalero en ${fullName}?`,
      a: _avg ? `Los trabajadores en ${fullName} suelen cobrar desde alrededor de $${_avg}/hora, aunque el pago varía según el oficio, la experiencia y el trabajo.` : `El pago varía según el oficio, la experiencia y el tipo de trabajo; casi todo se acuerda por hora o por trabajo directamente con quien contrata.` },
    { q: `¿Cómo consigo trabajo en ${fullName}?`,
      a: `Crea un perfil gratis en ChambaNow, indica tus habilidades y disponibilidad, y postúlate directamente a trabajos en ${fullName}: los trabajadores nunca pagan comisiones.` },
  ] : [
    { q: `What day-labor and trade jobs are hiring in ${fullName}?`,
      a: `ChambaNow lists ${_activeCount} active day-labor, trade and service jobs in and around ${fullName}${_skillLabels.length ? `, with demand for ${_skillLabels.slice(0, 3).join(", ")}` : ""}. New local jobs are posted regularly.` },
    { q: `How much do day-labor jobs in ${fullName} pay?`,
      a: _avg ? `Day-labor work through ChambaNow in ${fullName} typically starts around $${_avg}/hour, though pay varies by trade, experience and the specific job.` : `Pay varies by trade, experience and job type; most day-labor and trade work is set at an hourly or per-job rate you agree on directly with the hirer.` },
    { q: `How do I get hired for jobs in ${fullName}?`,
      a: `Create a free ChambaNow worker profile, list your skills and availability, and apply to local ${fullName} jobs directly. Workers never pay any fees.` },
  ];
  const faqsToShow = skillKey ? (lang === "es" ? [
    { q: `¿Qué trabajos de ${skillLabel.toLowerCase()} hay en ${fullName}?`,
      a: `ChambaNow tiene ${shownJobs.length + shownPartner.length} trabajos de ${skillLabel.toLowerCase()} y afines en ${fullName} y sus alrededores. Se publican nuevos con frecuencia; postúlate gratis.` },
    { q: `¿Cuánto pagan los trabajos de ${skillLabel.toLowerCase()} en ${fullName}?`,
      a: _avg ? `El trabajo de ${skillLabel.toLowerCase()} en ${fullName} suele empezar alrededor de $${_avg}/hora, aunque varía según la experiencia y el trabajo.` : `El pago de ${skillLabel.toLowerCase()} en ${fullName} varía según la experiencia y el trabajo; se acuerda por hora o por trabajo directamente.` },
    { q: `¿Cómo consigo trabajo de ${skillLabel.toLowerCase()} en ${fullName}?`,
      a: `Crea un perfil gratis, agrega ${skillLabel.toLowerCase()} a tus habilidades y postúlate a trabajos en ${fullName}: los trabajadores nunca pagan comisiones.` },
  ] : [
    { q: `What ${skillLabel.toLowerCase()} jobs are available in ${fullName}?`,
      a: `ChambaNow lists ${shownJobs.length + shownPartner.length} ${skillLabel.toLowerCase()} and related jobs in and around ${fullName}. New local ${skillLabel.toLowerCase()} work is posted regularly — apply directly, free.` },
    { q: `How much do ${skillLabel.toLowerCase()} jobs in ${fullName} pay?`,
      a: _avg ? `${skillLabel} work in ${fullName} typically starts around $${_avg}/hour, though it varies by experience, the employer and the job.` : `${skillLabel} pay in ${fullName} varies by experience and job — most is set at an hourly or per-job rate you agree on directly.` },
    { q: `How do I get hired for ${skillLabel.toLowerCase()} work in ${fullName}?`,
      a: `Create a free ChambaNow profile, add ${skillLabel.toLowerCase()} to your skills, and apply to local ${fullName} jobs directly. Workers never pay any fees.` },
  ]) : faqs;
  jsonLd.push({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqsToShow.map((f) => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })),
  });

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-10" data-testid={`city-jobs-${city.slug}`}>
      <nav className="text-xs text-zinc-500 flex items-center gap-1.5 mb-4 flex-wrap" aria-label="Breadcrumb">
        <Link to="/jobs" className="hover:text-[#FF7A1A] inline-flex items-center gap-1">
          <ArrowLeft size={11} weight="bold" /> {t.cityJobs.allJobs}
        </Link>
        <span>/</span>
        {skillLabel ? (
          <>
            <Link to={`/jobs/in/${city.slug}`} className="hover:text-[#FF7A1A]">{fullName}</Link>
            <span>/</span>
            <span className="text-zinc-300">{skillLabel}</span>
          </>
        ) : (
          <span className="text-zinc-300">{fullName}</span>
        )}
      </nav>

      <div className="card-surface p-6 md:p-10 bg-gradient-to-br from-[#FF7A1A]/15 via-zinc-950 to-zinc-950 border-[#FF7A1A]/30">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-md bg-[#FF7A1A] flex items-center justify-center">
            <MapTrifold size={26} weight="fill" color="#fff" />
          </div>
          <span className="text-xs font-bold tracking-[0.25em] text-[#FF7A1A] uppercase">
            {t.cityJobs.eyebrow}{city.state ? ` · ${city.state}` : ""}
          </span>
        </div>
        <h1 className="mt-4 font-display text-3xl md:text-5xl uppercase tracking-tighter text-white" data-testid="city-jobs-h1">
          {skillLabel ? (lang === "es" ? `Trabajos de ${skillLabel} en ${fullName}` : `${skillLabel} Jobs in ${fullName}`) : (<>{t.cityJobs.heroPrefix} {fullName}</>)}
        </h1>
        <p className="mt-3 text-zinc-300 max-w-3xl" data-testid="city-jobs-intro">{skillKey ? skillDesc : (intro || blurb)}</p>
        {skillKey && skillDesc2 && (
          <p className="mt-3 text-zinc-400 max-w-3xl text-sm leading-relaxed" data-testid="city-jobs-skill-desc2">{skillDesc2}</p>
        )}
        <div className="mt-5 flex flex-wrap gap-2 items-center">
          <a href="#city-open-jobs" className="btn-pill-primary" data-testid="city-jobs-cta-browse">
            {t.cityJobs.browseJobsCta}
          </a>
          <Link to="/signup?role=laborer" className="btn-pill-outline" data-testid="city-jobs-cta-signup">
            {t.cityJobs.workerSignupCta}
          </Link>
          <Link to={`/hire/${city.slug}`} className="text-xs text-zinc-400 hover:text-[#FF7A1A] transition-colors ml-1" data-testid="city-jobs-cta-hire">
            {t.cityJobs.hiringLink} &rarr;
          </Link>
        </div>
        <div className="mt-6 pt-5 border-t border-white/10">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400 mb-2">
            {lang === "es" ? `Comparte los trabajos en ${fullName}` : `Share jobs in ${fullName}`}
          </div>
          <ShareButtons
            url={`https://chambanow.com/api/share/city/${city.slug}`}
            message={lang === "es"
              ? `Trabajos disponibles ahora en ${fullName} — ¡postúlate gratis en ChambaNow!`
              : `Jobs hiring now in ${fullName} — apply free on ChambaNow!`}
            copyLabel={lang === "es" ? "Copiar enlace" : "Copy link"}
            copiedLabel={lang === "es" ? "¡Copiado!" : "Copied!"}
          />
        </div>
      </div>

      {supply?.top_skills?.length > 0 && (
        <div className="mt-4" data-testid="city-jobs-skills">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400 mb-2">
            {lang === "es" ? `Oficios más solicitados en ${fullName}` : `In-demand skills in ${fullName}`}
          </div>
          <div className="flex flex-wrap gap-2">
            {supply.top_skills.map((s) => (
              <Link key={s.skill} to={`/jobs/in/${citySlug}/${s.skill}`} className={skillKey === s.skill ? "chip-orange" : "chip-static hover:border-[#FF7A1A]/50"} data-testid={`city-jobs-skill-link-${s.skill}`}>
                {sk[s.skill] || s.skill.replace(/_/g, " ")}
                <span className="text-[10px] text-zinc-600"> · {s.count}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div id="city-open-jobs" />

      {(loading || shownJobs.length > 0 || shownPartner.length === 0) && (
        <>
          <h2 className="mt-10 font-display text-xl md:text-2xl uppercase tracking-tighter text-white">
            {t.cityJobs.openJobs} · {fullName}
          </h2>
          <p className="mt-1 text-sm text-zinc-500">
            {loading ? t.jobs.loading : `${shownJobs.length} ${shownJobs.length === 1 ? t.cityJobs.activeJob : t.cityJobs.activeJobs}`}
          </p>

          <div className="mt-5 space-y-3">
            {loading ? (
              <div className="card-surface p-8 text-center text-zinc-400">{t.jobs.loading}</div>
            ) : shownJobs.length === 0 ? (
              <div className="card-surface p-10 text-center" data-testid="city-jobs-empty">
                <p className="text-zinc-400">{t.cityJobs.empty.replace("{city}", fullName)}</p>
                <Link to="/jobs" className="btn-pill-outline mt-5 inline-flex">{t.cityJobs.browseAllJobs}</Link>
              </div>
            ) : (
              shownJobs.map((j) => (
                <Link
                  key={j.job_id}
                  to={`/jobs/${j.job_id}`}
                  className="card-surface p-5 block hover:border-[#FF7A1A]/50"
                  data-testid={`city-job-row-${j.job_id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <TranslatedText
                        text={j.title}
                        as="div"
                        className="font-display text-xl uppercase tracking-tight text-white truncate"
                        showToggle={false}
                      />
                      <div className="text-xs text-zinc-500 mt-1 flex items-center gap-3 flex-wrap">
                        <span className="flex items-center gap-1"><Tag size={11} weight="fill" /> <TagLabel kind="industry" code={j.industry} /></span>
                        <span className="flex items-center gap-1"><Briefcase size={11} weight="fill" /> <TagLabel kind="employment" code={j.employment_type} /></span>
                        <span className="flex items-center gap-1"><MapPin size={11} weight="fill" /> {j.city ? `${j.city}, ` : ""}{j.zip_code}</span>
                        {j.duration_hours && <span className="flex items-center gap-1"><Clock size={11} weight="fill" /> {j.duration_hours}h</span>}
                        <span className="flex items-center gap-1"><Truck size={11} weight="fill" /> {j.transport_included ? t.jobs.transport : t.jobs.self}</span>
                      </div>
                      <TranslatedText
                        text={j.description}
                        as="p"
                        className="mt-2 text-sm text-zinc-300 line-clamp-2"
                        showToggle={false}
                      />
                    </div>
                    <div className="text-right flex-shrink-0">
                      <span className="chip-orange whitespace-nowrap">
                        ${j.rate_amount}{(j.country || "").toUpperCase() === "MX" ? " MXN" : ""}/{j.rate_type === "hourly" ? t.jobs.perHr : j.rate_type === "salary" ? t.jobs.perYr : t.jobs.perJob}
                      </span>
                      {j.is_temporary && <div className="mt-1 text-[9px] font-black tracking-widest text-zinc-500">{t.jobs.temp}</div>}
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>
        </>
      )}

      <FbGroupInvite
        location={`${city.name}${city.state ? `, ${city.state}` : ""}`}
        country={cityState === "MX" ? "MX" : "US"}
        where="city_jobs_mid"
        className="mt-6"
      />

      {shownPartner.length > 0 && (
        <div className="mt-10" data-testid="city-jobs-partner-section">
          <div className="flex items-center gap-2">
            <Handshake size={18} weight="fill" className="text-[#FF7A1A]" />
            <h2 className="font-display text-xl md:text-2xl uppercase tracking-tighter text-white">{shownJobs.length === 0 ? `${t.cityJobs.jobsInPrefix} ${fullName}` : t.jobs.partnerSectionTitle}</h2>
          </div>
          <p className="mt-1 text-sm text-zinc-500">{t.jobs.partnerHint}</p>
          <div className="mt-5 space-y-3">
            {shownPartner.map((e, i) => (
              <Link
                key={e.external_id || i}
                to={`/partner-jobs/${e.slug}`}
                className="card-surface p-5 block hover:border-[#FF7A1A]/50"
                data-testid={`city-external-job-row-${i}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <span className="chip-orange !text-[9px] !py-0.5 inline-flex items-center gap-1">
                      <Handshake size={10} weight="fill" /> {t.jobs.partnerBadge}
                    </span>
                    <div className="font-display text-xl uppercase tracking-tight text-white truncate mt-1.5">{e.title}</div>
                    <div className="text-xs text-zinc-500 mt-1 flex items-center gap-3 flex-wrap">
                      {e.company && <span className="flex items-center gap-1"><Briefcase size={11} weight="fill" /> {e.company}</span>}
                      {e.location && <span className="flex items-center gap-1"><MapPin size={11} weight="fill" /> {e.location}</span>}
                      {e.type && <span className="flex items-center gap-1"><Tag size={11} weight="fill" /> {e.type}</span>}
                    </div>
                    {e.snippet && <p className="mt-2 text-sm text-zinc-300 line-clamp-2">{e.snippet}</p>}
                  </div>
                  <div className="text-right flex-shrink-0 flex flex-col items-end gap-2">
                    {e.salary && <span className="chip-orange whitespace-nowrap">{e.salary}</span>}
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-[#FF7A1A]">
                      {t.jobs.partnerListApply} <ArrowSquareOut size={12} weight="bold" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          {!skillKey && externalJobs.length < partnerTotal && (
            <button onClick={loadMorePartner} className="btn-pill-outline mt-5" data-testid="city-partner-load-more">
              {t.cityJobs.loadMore || "Load more"} ({externalJobs.length}/{partnerTotal})
            </button>
          )}
        </div>
      )}

      <FbGroupInvite
        location={`${city.name}${city.state ? `, ${city.state}` : ""}`}
        country={cityState === "MX" ? "MX" : "US"}
        where="city_jobs_end"
        className="mt-10"
      />

      <div className="mt-12" data-testid="city-jobs-faq">
        <h2 className="font-display text-xl md:text-2xl uppercase tracking-tighter text-white">
          {skillLabel ? (lang === "es" ? `Preguntas frecuentes · ${skillLabel} en ${fullName}` : `FAQ · ${skillLabel} jobs in ${fullName}`) : (lang === "es" ? `Preguntas frecuentes · ${fullName}` : `FAQ · Jobs in ${fullName}`)}
        </h2>
        <div className="mt-5 space-y-3">
          {faqsToShow.map((f, i) => (
            <div key={i} className="card-surface p-5" data-testid={`city-jobs-faq-${i}`}>
              <div className="font-display text-base uppercase tracking-tight text-white">{f.q}</div>
              <p className="mt-2 text-sm text-zinc-300">{f.a}</p>
            </div>
          ))}
        </div>
      </div>

      {nearby.length > 0 && (
        <div className="mt-10" data-testid="city-jobs-nearby">
          <div className="flex items-center gap-2">
            <MapPin size={16} weight="fill" className="text-[#FF7A1A]" />
            <h2 className="font-display text-lg md:text-xl uppercase tracking-tight text-white">{t.cityJobs.nearbyTitle}</h2>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {nearby.map((c) => (
              <Link
                key={c.slug}
                to={`/jobs/in/${c.slug}`}
                className="text-sm text-zinc-300 border border-white/10 hover:border-[#FF7A1A]/60 hover:text-[#FF7A1A] rounded-full px-3.5 py-1.5 transition-colors"
                data-testid={`city-jobs-nearby-${c.slug}`}
              >
                {c.name}, {c.state} <span className="text-zinc-600 text-xs">· {Math.round(c.distance_miles)} mi</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {skillKey && nearby.length > 0 && (
        <div className="mt-10" data-testid="city-jobs-nearby-skill">
          <div className="flex items-center gap-2">
            <MapPin size={16} weight="fill" className="text-[#FF7A1A]" />
            <h2 className="font-display text-lg md:text-xl uppercase tracking-tight text-white">
              {lang === "es" ? `Trabajos de ${skillLabel.toLowerCase()} en ciudades cercanas` : `${skillLabel} jobs in nearby cities`}
            </h2>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {nearby.map((c) => (
              <Link
                key={c.slug}
                to={`/jobs/in/${c.slug}/${skillKey}`}
                className="text-sm text-zinc-300 border border-white/10 hover:border-[#FF7A1A]/60 hover:text-[#FF7A1A] rounded-full px-3.5 py-1.5 transition-colors"
                data-testid={`city-jobs-nearby-skill-${c.slug}`}
              >
                {skillLabel} {lang === "es" ? "en" : "in"} {c.name} <span className="text-zinc-600 text-xs">· {Math.round(c.distance_miles)} mi</span>
              </Link>
            ))}
          </div>
        </div>
      )}


      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
    </div>
  );
}
